import html2canvas from 'html2canvas';
import {jsPDF as JsPDF} from 'jspdf';

const DEFAULT_HTML2CANVAS = {
  scale: 2,
  useCORS: true,
  logging: false,
};

// On-chart value labels drawn for the static export (see drawValuesOnChart).
const LABEL_FONT = 'bold 11px sans-serif';
const LABEL_DARK = '#374151'; // outside a mark, on the white report background
const LABEL_LIGHT = '#ffffff'; // over a colored mark, paired with an outline
const LABEL_OUTLINE = 'rgba(0,0,0,0.6)';
const TINY_ARC_FRACTION = 0.06; // slice small enough to warrant a heavier outline
const MIN_STACKED_SEGMENT_PX = 14; // stacked segment thinner than this can't hold a label
const BAR_LABEL_GAP = 6; // px between a bar end and its label
const BAR_HEADROOM_PX = 14; // clearance a vertical label needs above a bar/point

/**
 * @param {Element} el - DOM element; may be from an iframe (use nodeType, not instanceof HTMLElement).
 * @param {Object} [options] - Passed to html2canvas (scale, useCORS, etc.)
 * @return {Promise<HTMLCanvasElement>}
 */
export async function captureElementToCanvas(el, options = {}) {
  // Iframe document.documentElement/body are ELEMENT_NODE but fail parent-realm `instanceof HTMLElement`.
  if (!el || el.nodeType !== Node.ELEMENT_NODE || !el.ownerDocument) {
    throw new Error('captureElementToCanvas requires a DOM element');
  }
  return html2canvas(el, {
    ...DEFAULT_HTML2CANVAS,
    ...options,
  });
}

/**
 * Draw one chart's data values onto a target 2D context, at the point the hover tooltip
 * would anchor (element.tooltipPosition()). Geometry is read from the live chart, but the
 * caller passes the context of html2canvas's *cloned* canvas — so the live chart is never
 * modified (no on-screen flash, no dependency on a live redraw surviving the capture).
 * @param {Object} chart - a live Chart.js instance (source of geometry/data)
 * @param {CanvasRenderingContext2D} ctx - target context (the cloned canvas)
 * @param {number} dpr - device pixel ratio the chart rendered at (element coords are CSS px)
 * @return {void}
 */
function drawValuesOnChart(chart, ctx, dpr) {
  const area = chart.chartArea;
  if (!ctx || !area) {
    return;
  }
  ctx.save();
  ctx.scale(dpr, dpr); // element coords are CSS px; the clone's raw context is unscaled
  ctx.font = LABEL_FONT;
  (chart.data?.datasets || []).forEach((ds, di) => {
    if (ds.valueLabels === false) {
      return; // dataset opted out (e.g. a flat target/reference line)
    }
    const meta = chart.getDatasetMeta(di);
    if (!meta || meta.hidden) {
      return;
    }
    const type = meta.type || chart.config?.type;
    const scales = chart.options?.scales;
    const isArc = type === 'doughnut' || type === 'pie';
    const isBar = type === 'bar';
    const horizontal = isBar && chart.options?.indexAxis === 'y';
    // Stacked along the value axis (y for vertical bars, x for horizontal), or via dataset.stack.
    const stackedAxis = horizontal ? scales?.x?.stacked : scales?.y?.stacked;
    const isStacked = isBar && (!!stackedAxis || ds.stack != null);
    const total = isArc
      ? ds.data.reduce((sum, n) => {
          return sum + (Number(n) || 0);
        }, 0)
      : 0;
    (meta.data || []).forEach((el, i) => {
      const raw = ds.data[i];
      if (raw == null || !el || typeof el.tooltipPosition !== 'function') {
        return;
      }
      const v = Number(raw);
      if (Number.isNaN(v)) {
        return; // non-numeric (e.g. {x,y} scatter data) — nothing to label
      }
      if (isArc && v <= 0) {
        return; // a zero-value slice has no arc to label
      }
      const text =
        typeof ds.valueFormatter === 'function'
          ? ds.valueFormatter(v)
          : v.toLocaleString(undefined, {maximumFractionDigits: 1});
      const p = el.tooltipPosition();
      const halfText = ctx.measureText(text).width / 2;

      if (isArc) {
        // Label every slice at its arc anchor; thin slices get a heavier outline so the
        // white value stays legible where it spills over neighbors (clip-proof).
        const frac = total ? v / total : 1;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineWidth = frac < TINY_ARC_FRACTION ? 4 : 3;
        ctx.strokeStyle = LABEL_OUTLINE;
        ctx.strokeText(text, p.x, p.y);
        ctx.fillStyle = LABEL_LIGHT;
        ctx.fillText(text, p.x, p.y);
      } else if (isStacked) {
        // Center the value in its segment; skip segments too thin to hold a label.
        const segPx = horizontal ? Math.abs(el.x - el.base) : Math.abs(el.base - el.y);
        if (segPx < MIN_STACKED_SEGMENT_PX) {
          return;
        }
        const sx = horizontal ? (el.x + el.base) / 2 : el.x;
        const sy = horizontal ? el.y : (el.y + el.base) / 2;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineWidth = 3;
        ctx.strokeStyle = LABEL_OUTLINE;
        ctx.strokeText(text, sx, sy);
        ctx.fillStyle = LABEL_LIGHT;
        ctx.fillText(text, sx, sy);
      } else if (horizontal) {
        // Just past the bar end; if that would overflow the plot, draw inside the end (white).
        if (p.x + BAR_LABEL_GAP + halfText * 2 <= area.right) {
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = LABEL_DARK;
          ctx.fillText(text, p.x + BAR_LABEL_GAP, p.y);
        } else {
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          ctx.lineWidth = 3;
          ctx.strokeStyle = LABEL_OUTLINE;
          ctx.strokeText(text, p.x - BAR_LABEL_GAP, p.y);
          ctx.fillStyle = LABEL_LIGHT;
          ctx.fillText(text, p.x - BAR_LABEL_GAP, p.y);
        }
      } else {
        // Vertical bar or line point: clamp x so first/last labels don't clip at the edges.
        const cx = Math.max(area.left + halfText, Math.min(p.x, area.right - halfText));
        if (p.y - BAR_HEADROOM_PX < area.top) {
          // Not enough headroom above — draw inside the bar top, white with outline.
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.lineWidth = 3;
          ctx.strokeStyle = LABEL_OUTLINE;
          ctx.strokeText(text, cx, p.y + 4);
          ctx.fillStyle = LABEL_LIGHT;
          ctx.fillText(text, cx, p.y + 4);
        } else {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillStyle = LABEL_DARK;
          ctx.fillText(text, cx, p.y - 4);
        }
      }
    });
  });
  ctx.restore();
}

/**
 * During an html2canvas capture, draw each chart's values onto the CLONED canvas (which
 * html2canvas will rasterize) rather than the live one. The live charts are never touched,
 * so there is no on-screen flash, no risk of a live redraw wiping the labels mid-capture,
 * and concurrent exports cannot interfere with each other. Deterministic: reads the iframe's
 * live Chart.js instances, so it does not depend on the report HTML including any plugin.
 * @param {Window} win - iframe contentWindow (must expose Chart)
 * @param {Document} liveDoc - the live iframe document (source charts)
 * @param {Document} clonedDoc - html2canvas's cloned document (render target)
 * @return {void}
 */
function labelClonedCharts(win, liveDoc, clonedDoc) {
  const ChartLib = win && win.Chart;
  const liveCanvases = liveDoc ? liveDoc.querySelectorAll('canvas') : [];
  const clonedCanvases = clonedDoc ? clonedDoc.querySelectorAll('canvas') : [];
  if (!ChartLib || typeof ChartLib.getChart !== 'function') {
    if (clonedCanvases.length) {
      // eslint-disable-next-line no-console
      console.warn('[insights export] Chart global not found in report iframe; charts exported without value labels.');
    }
    return;
  }
  // Prefer id matching (order-independent); fall back to index only when counts line up.
  const clonedById = {};
  clonedCanvases.forEach((c) => {
    if (c.id) {
      clonedById[c.id] = c;
    }
  });
  const sameCount = clonedCanvases.length === liveCanvases.length;
  liveCanvases.forEach((liveCv, idx) => {
    const chart = ChartLib.getChart(liveCv);
    if (!chart) {
      return; // canvas isn't a Chart.js chart (or not yet initialized)
    }
    const cloned = (liveCv.id && clonedById[liveCv.id]) || (sameCount ? clonedCanvases[idx] : null);
    const clonedCtx = cloned && cloned.getContext('2d');
    if (!clonedCtx) {
      return;
    }
    const cssWidth = liveCv.clientWidth || liveCv.width;
    const dpr = chart.currentDevicePixelRatio || (cssWidth ? liveCv.width / cssWidth : 1) || 1;
    try {
      drawValuesOnChart(chart, clonedCtx, dpr);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[insights export] Failed to label a chart for export:', e);
    }
  });
}

/**
 * @param {HTMLIFrameElement} iframeEl
 * @param {Object} [options] - html2canvas options; `delayMs` waits before capture (Chart.js)
 * @return {Promise<HTMLCanvasElement>}
 */
export async function captureIframeDocumentToCanvas(iframeEl, options = {}) {
  const doc = iframeEl?.contentDocument;
  const target = doc?.documentElement || doc?.body;
  if (!target) {
    throw new Error('Iframe document not available for capture');
  }
  const win = iframeEl?.contentWindow;
  const delayMs = options.delayMs ?? 350;
  // Let Chart.js finish its initial paint before we snapshot.
  await new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
  const {delayMs: _omit, onclone: callerOnClone, ...rest} = options;
  // Values are drawn onto html2canvas's clone of each chart (never the live canvas), so the
  // interactive charts stay clean, there is no flash, and concurrent exports don't interfere.
  return captureElementToCanvas(target, {
    ...rest,
    onclone: (clonedDoc, clonedEl) => {
      try {
        labelClonedCharts(win, doc, clonedDoc);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[insights export] Chart labeling skipped:', e);
      }
      return typeof callerOnClone === 'function' ? callerOnClone(clonedDoc, clonedEl) : undefined;
    },
  });
}

/**
 * Stack images vertically (top then bottom), white background.
 * @param {HTMLCanvasElement} top
 * @param {HTMLCanvasElement} bottom
 * @return {HTMLCanvasElement}
 */
export function mergeCanvasesVertical(top, bottom) {
  const w = Math.max(top.width, bottom.width);
  const h = top.height + bottom.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(top, 0, 0);
  ctx.drawImage(bottom, 0, top.height);
  return canvas;
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {string} filename
 * @return {void}
 */
export function downloadPngFromCanvas(canvas, filename) {
  const name = filename.endsWith('.png') ? filename : `${filename}.png`;
  const link = document.createElement('a');
  link.download = name;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

/**
 * Add one canvas as one or more portrait A4 pages (tall images split).
 * @param {Object} pdf - jsPDF instance
 * @param {HTMLCanvasElement} canvas
 * @param {number} marginMm
 */
function addCanvasToPdfMultiPage(pdf, canvas, marginMm) {
  const imgData = canvas.toDataURL('image/png');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const innerW = pageWidth - 2 * marginMm;
  const innerH = pageHeight - 2 * marginMm;

  const imgW = innerW;
  const imgH = (canvas.height * imgW) / canvas.width;

  let y = marginMm;
  pdf.addImage(imgData, 'PNG', marginMm, y, imgW, imgH);

  let heightLeft = imgH - innerH;
  while (heightLeft > 0) {
    pdf.addPage();
    y = marginMm - (imgH - heightLeft);
    pdf.addImage(imgData, 'PNG', marginMm, y, imgW, imgH);
    heightLeft -= innerH;
  }
}

/**
 * PDF with one section per canvas; each section may span multiple pages if tall.
 * @param {HTMLCanvasElement[]} canvases
 * @param {string} filename
 * @return {void}
 */
export function downloadPdfFromCanvases(canvases, filename) {
  if (!canvases.length) {
    throw new Error('No canvases to export');
  }
  const name = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  const marginMm = 10;
  const pdf = new JsPDF({orientation: 'p', unit: 'mm', format: 'a4'});

  canvases.forEach((canvas, i) => {
    if (i > 0) {
      pdf.addPage();
    }
    addCanvasToPdfMultiPage(pdf, canvas, marginMm);
  });

  pdf.save(name);
}
